import { Print } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentsEntryTypes } from '~/data/documents';
import { PendingScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useSchedulePendingEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useSchedulePendingEmployment';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useMenu } from '~/hooks/useMenu';
import { usePrintWorkContracts } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/components/DocumentPrintMenu/usePrintWorkContracts';

import { DocumentsEntry } from '../../data';
import { PendingEmploymentsMenu } from '../PendingEmploymentsMenu';

export const DocumentPrintMenu = () => {
  const [pendingEmploymentSelected, setPendingEmploymentSelected] =
    useState<PendingScheduleChanges | null>(null);

  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { data: pendingEmploymentDetails } =
    useSchedulePendingEmployment(selectedEmployeeId);

  const pendingEmploymentExist = pendingEmploymentDetails?.some(
    (empDetails) => Object.keys(empDetails || {}).length,
  );

  // Print Menu
  const { onPrintWorkContract, workContractDocuments: printFiles } =
    usePrintWorkContracts(pendingEmploymentSelected?.employment?.fEmplId);

  const printMenu = useMenu();

  const onPrintFile = useCallback(
    (entry: DocumentsEntry, selectedEmployment?: PendingScheduleChanges) => {
      onPrintWorkContract(entry.id, selectedEmployment)
        .then(() => {
          printMenu.close();
        })
        .catch(() => {
          printMenu.close();
        });
    },
    [onPrintWorkContract, printMenu],
  );

  const printMenuList = useMemo<DocumentsEntry[]>(() => {
    return printFiles.filter(
      (file) =>
        file.type === DocumentsEntryTypes.WORK_CONTRACT_TEMPLATE ||
        file.type === DocumentsEntryTypes.WORK_CERTIFICATE_TEMPLATE,
    );
  }, [printFiles]);

  const MenuListOptions = useMemo(() => {
    return printMenuList.map((file, index) => {
      return (
        <MenuItem
          onClick={() =>
            onPrintFile(file, pendingEmploymentSelected || undefined)
          }
          key={`print-document-${index}-${file.id}`}
        >
          {file.description || file.name}
        </MenuItem>
      );
    });
  }, [onPrintFile, printMenuList, pendingEmploymentSelected]);

  const handleChangeEmployment = (
    selectedPendingEmployment: PendingScheduleChanges | null,
  ) => {
    setPendingEmploymentSelected(selectedPendingEmployment);
  };

  return (
    <>
      <Button
        color="primary"
        startIcon={<Print />}
        onClick={printMenu.open}
        disabled={!printFiles.length}
      >
        {t('print')}
      </Button>
      <Menu
        open={printMenu.isOpen}
        anchorEl={printMenu.anchorEl}
        onClose={printMenu.close}
      >
        {pendingEmploymentExist ? (
          <PendingEmploymentsMenu
            selectedEmployeeId={selectedEmployeeId}
            handleChangeEmployment={handleChangeEmployment}
          />
        ) : (
          MenuListOptions
        )}
        {pendingEmploymentExist && pendingEmploymentSelected
          ? MenuListOptions
          : null}
      </Menu>
    </>
  );
};
